import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Fade,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { format } from 'date-fns';
import TipTapEditor from '../components/common/TipTapEditor';
import { findingKlasseName, findingRefTargets } from '../utils/schwachstelleReferenz';
import { auditsService } from '../services/auditsService';
import { filesService } from '../services/filesService';
import { schwachstellenService } from '../services/schwachstellenService';
import { durchfuehrungszeitraeumeService } from '../services/durchfuehrungszeitraeumeService';
import { projekteService } from '../services/projekteService';
import {
  auditTypesService,
  auditStatusService,
} from '../services/lookupService';
import {
  Audit,
  AuditType,
  AuditStatus,
  Durchfuehrungszeitraum,
  FileEntity,
  ProjektList,
  Schwachstelle,
  SchwachstelleList,
} from '../types';
import {
  formatSeverityLower,
  getCvssSeverityLabelLower,
  getKritikalitaetHexColor,
} from '../utils/severity';
import {
  buildKritData,
  buildReviewData,
  getKritColorMap,
} from './audit-detail/auditDetailStats';
import { AuditChartDatum } from './audit-detail/auditDetailTypes';

/* ═══════════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════════ */

const RAIL_WIDTH = 260;
const HEADER_HEIGHT = 52;

type Selection = 'overview' | 'statistics' | number;

/* ═══════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════ */

function buildReferenzId(projektkennung: string | undefined, index: number): string {
  const prefix = (projektkennung || 'SV').trim() || 'SV';
  return `${prefix}-${String(index + 1).padStart(3, '0')}`;
}

function getCvssSeverityColor(score?: number): string {
  if (score === undefined || score === null) return '#78909c';
  if (score === 0) return '#78909c';
  if (score <= 3.9) return '#4caf50';
  if (score <= 6.9) return '#ff9800';
  if (score <= 8.9) return '#f44336';
  return '#9c27b0';
}

/* ═══════════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════════ */

const AuditDemoModePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const auditId = id ? Number.parseInt(id, 10) : null;

  /* ── State ───────────────────────────────────────────────────── */
  const [audit, setAudit] = useState<Audit | null>(null);
  const [schwachstellenList, setSchwachstellenList] = useState<SchwachstelleList[]>([]);
  const [schwachstellenDetails, setSchwachstellenDetails] = useState<Record<number, Schwachstelle>>({});
  const [zeitraeume, setZeitraeume] = useState<Durchfuehrungszeitraum[]>([]);
  const [projekte, setProjekte] = useState<ProjektList[]>([]);
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [auditStatuses, setAuditStatuses] = useState<AuditStatus[]>([]);
  const [schwachstellenFiles, setSchwachstellenFiles] = useState<Record<number, FileEntity[]>>({});
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<Selection>('overview');
  const findingReferenceLang: 'de' | 'en' = audit?.sprache === 'en' ? 'en' : 'de';
  const findingReferences = useMemo(
    () => findingRefTargets(schwachstellenList.map((row) => ({
      id: row.id,
      titel: row.titel,
      klasse: findingKlasseName(row.schwachstellenklasse, findingReferenceLang),
    }))),
    [schwachstellenList, findingReferenceLang],
  );

  /* ── Lightbox state ──────────────────────────────────────────── */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<FileEntity[]>([]);
  const [lightboxUsedIds, setLightboxUsedIds] = useState<Set<number>>(new Set());
  const [lightboxIndex, setLightboxIndex] = useState(0);

  /* ── Data loading ────────────────────────────────────────────── */
  useEffect(() => {
    if (!auditId) return;

    const load = async () => {
      try {
        setLoading(true);
        const [auditData, findingsList, zeitraeumeData, projekteData, typesData, statusesData] =
          await Promise.all([
            auditsService.get(auditId),
            schwachstellenService.list({ audit_id: auditId }),
            durchfuehrungszeitraeumeService.list({ audit_id: auditId }),
            projekteService.list(),
            auditTypesService.list(),
            auditStatusService.list(),
          ]);
        setAudit(auditData);
        setSchwachstellenList(findingsList);
        setZeitraeume(zeitraeumeData);
        setProjekte(projekteData);
        setAuditTypes(typesData);
        setAuditStatuses(statusesData);
      } catch (error) {
        console.error('Fehler beim Laden der Demo-Daten:', error);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [auditId]);

  // Prefetch full details and files for all findings once the list is loaded
  useEffect(() => {
    if (schwachstellenList.length === 0) return;

    const fetchDetails = async () => {
      const [detailResults, fileResults] = await Promise.all([
        Promise.allSettled(schwachstellenList.map((s) => schwachstellenService.get(s.id))),
        Promise.allSettled(schwachstellenList.map((s) => filesService.list({ schwachstelle_id: s.id }))),
      ]);

      const detailMap: Record<number, Schwachstelle> = {};
      detailResults.forEach((r) => {
        if (r.status === 'fulfilled') detailMap[r.value.id] = r.value;
      });
      setSchwachstellenDetails(detailMap);

      const fileMap: Record<number, FileEntity[]> = {};
      fileResults.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          fileMap[schwachstellenList[i].id] = r.value.filter((f) => {
            const ext = f.dateiname.split('.').pop()?.toLowerCase() || '';
            return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
          });
        }
      });
      setSchwachstellenFiles(fileMap);
    };

    void fetchDetails();
  }, [schwachstellenList]);

  /* ── Derived data ────────────────────────────────────────────── */
  const projektName = useMemo(
    () => projekte.find((p) => p.id === audit?.projekt_id)?.name,
    [projekte, audit],
  );

  const auditTypeNames = useMemo(
    () =>
      (audit?.audit_type_ids || [])
        .map((tid) => auditTypes.find((t) => t.id === tid))
        .filter(Boolean) as AuditType[],
    [audit, auditTypes],
  );

  const auditStatusName = useMemo(() => {
    if (!audit?.audit_status_id) return undefined;
    return auditStatuses.find((s) => s.id === audit.audit_status_id);
  }, [audit, auditStatuses]);

  const kritData = useMemo(
    () => buildKritData(schwachstellenList, isDark),
    [schwachstellenList, isDark],
  );

  const reviewData = useMemo(
    () => buildReviewData(schwachstellenList),
    [schwachstellenList],
  );

  /* ── Styles ──────────────────────────────────────────────────── */
  const labelCellSx = {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    bgcolor: isDark ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.grey[100], 0.8),
    border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.2) : theme.palette.divider}`,
    width: '170px',
    py: 1,
    px: 1.5,
    whiteSpace: 'nowrap' as const,
  };

  const valueCellSx = {
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.2) : theme.palette.divider}`,
    py: 1,
    px: 1.5,
  };

  /* ── Rail button helper ──────────────────────────────────────── */
  const navBtnSx = useCallback(
    (active: boolean, color?: string) => ({
      width: '100%',
      display: 'flex',
      flexDirection: 'row' as const,
      alignItems: 'center',
      gap: 1,
      py: 1,
      px: 1.25,
      borderRadius: 2,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      bgcolor: active
        ? isDark
          ? alpha(color || theme.palette.primary.main, 0.15)
          : alpha(color || theme.palette.primary.main, 0.08)
        : 'transparent',
      borderLeft: active ? `3px solid ${color || theme.palette.primary.main}` : '3px solid transparent',
      '&:hover': {
        bgcolor: isDark
          ? alpha(color || theme.palette.primary.main, 0.1)
          : alpha(color || theme.palette.primary.main, 0.05),
      },
    }),
    [isDark, theme],
  );

  /* ═══════════════════════════════════════════════════════════════
     Lightbox helpers
     ═══════════════════════════════════════════════════════════════ */
  const getUsedFileIds = useCallback((detail: Schwachstelle): Set<number> => {
    const combined = [
      detail.allgemeine_beschreibung || '',
      detail.detailbeschreibung || '',
      detail.mitigationsmassnahmen || '',
    ].join(' ');
    const ids = new Set<number>();
    const re = /\/files\/(\d+)\/preview/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(combined)) !== null) ids.add(Number(m[1]));
    return ids;
  }, []);

  const openLightbox = useCallback((images: FileEntity[], startIndex: number, usedIds: Set<number>) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxUsedIds(usedIds);
    setLightboxOpen(true);
  }, []);

  const contentRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen || lightboxImages.length <= 1) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImages.length]);

  // Click handler: intercept clicks on <img> inside TipTap content
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'IMG') return;

      const src = (target as HTMLImageElement).src;
      const fileIdMatch = src.match(/\/files\/(\d+)\/preview/);
      if (!fileIdMatch) return;

      const clickedFileId = Number(fileIdMatch[1]);
      if (typeof selection !== 'number') return;

      const listItem = schwachstellenList[selection];
      if (!listItem) return;

      const detail = schwachstellenDetails[listItem.id];
      const allFiles = schwachstellenFiles[listItem.id] || [];
      if (allFiles.length === 0) return;

      const usedIds = detail ? getUsedFileIds(detail) : new Set<number>();
      const idx = allFiles.findIndex((f) => f.id === clickedFileId);
      openLightbox(allFiles, idx >= 0 ? idx : 0, usedIds);
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [selection, schwachstellenList, schwachstellenDetails, schwachstellenFiles, getUsedFileIds, openLightbox]);

  /* ═══════════════════════════════════════════════════════════════
     Recharts tooltip
     ═══════════════════════════════════════════════════════════════ */
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null;
    const datum = payload[0];
    return (
      <Box
        sx={{
          bgcolor: isDark ? '#1e2444' : '#fff',
          border: `1px solid ${isDark ? '#2e3560' : '#dfe3e8'}`,
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: datum.payload.color || datum.payload.fill, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#e8edf3' : '#1a2332', fontSize: '0.8rem' }}>
          {datum.name}: {datum.value}
        </Typography>
      </Box>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     Overview panel
     ═══════════════════════════════════════════════════════════════ */
  const renderOverview = () => {
    if (!audit) return null;
    return (
      <Fade in key="overview">
        <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: 3 }}>
          {/* Audit Name */}
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: theme.palette.primary.main }}>
            {audit.name}
          </Typography>
          {projektName && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Projekt: {projektName}
            </Typography>
          )}

          {/* Meta cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            {/* Audit-Typen */}
            <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                  Audit-Typ
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {auditTypeNames.map((t) => (
                    <Chip
                      key={t.id}
                      label={t.name}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        bgcolor: t.farbe ? alpha(t.farbe, 0.15) : alpha(theme.palette.primary.main, 0.1),
                        color: t.farbe || theme.palette.primary.main,
                        border: `1px solid ${t.farbe ? alpha(t.farbe, 0.3) : alpha(theme.palette.primary.main, 0.3)}`,
                      }}
                    />
                  ))}
                  {auditTypeNames.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>–</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Status */}
            <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                  Status
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {auditStatusName?.name || '–'}
                </Typography>
              </CardContent>
            </Card>

            {/* Sprache & Projektkennung */}
            <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                  Sprache / Kennung
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {audit.sprache?.toUpperCase() || '–'}
                  {audit.projektkennung && (
                    <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
                      ({audit.projektkennung})
                    </Typography>
                  )}
                </Typography>
              </CardContent>
            </Card>

            {/* Findings count */}
            <Card sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                  Schwachstellen
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                  {schwachstellenList.length}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Preflight */}
          {audit.preflight_datum && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.5 }}>
                Preflight-Datum
              </Typography>
              <Typography variant="body2">
                {format(new Date(audit.preflight_datum), 'dd.MM.yyyy')}
              </Typography>
            </Box>
          )}

          {/* Team */}
          {audit.mitarbeiter_team && audit.mitarbeiter_team.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <GroupIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Pentester-Team
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {audit.mitarbeiter_team.map((m) => (
                    <Chip
                      key={m.id}
                      label={`${m.vorname} ${m.nachname}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Zeiträume */}
          {zeitraeume.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CalendarMonthIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Durchführungszeiträume
                  </Typography>
                </Box>
                {zeitraeume.map((z) => (
                  <Box key={z.id} sx={{ mb: 1.5, pl: 1, borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {format(new Date(z.startdatum), 'dd.MM.yyyy')} – {format(new Date(z.enddatum), 'dd.MM.yyyy')}
                    </Typography>
                    {z.beschreibung && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{z.beschreibung}</Typography>
                    )}
                    {z.mitarbeiter_team && z.mitarbeiter_team.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {z.mitarbeiter_team.map((m) => (
                          <Chip key={m.id} label={`${m.vorname} ${m.nachname}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Management Summary */}
          {audit.management_summary && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Management Summary
                </Typography>
                <TipTapEditor
                  content={audit.management_summary}
                  editable={false}
                  hidePageBreaks
                  minHeight={60}
                  findingReferences={findingReferences}
                  findingReferenceLang={findingReferenceLang}
                />
              </CardContent>
            </Card>
          )}
        </Box>
      </Fade>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     Statistics panel
     ═══════════════════════════════════════════════════════════════ */
  const renderStatistics = () => {
    const scores = schwachstellenList
      .map((s) => s.cvss31_score)
      .filter((s): s is number => s != null);

    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    const minScore = scores.length > 0 ? Math.min(...scores) : null;
    const maxScore = scores.length > 0 ? Math.max(...scores) : null;

    const buckets = [
      { label: 'None (0)', min: 0, max: 0, color: '#78909c' },
      { label: 'Low (0.1–3.9)', min: 0.1, max: 3.9, color: '#4caf50' },
      { label: 'Medium (4.0–6.9)', min: 4.0, max: 6.9, color: '#ff9800' },
      { label: 'High (7.0–8.9)', min: 7.0, max: 8.9, color: '#f44336' },
      { label: 'Critical (9.0–10.0)', min: 9.0, max: 10.0, color: '#9c27b0' },
    ];

    const bucketData = buckets.map((b) => ({
      name: b.label,
      value: scores.filter((s) => s >= b.min && s <= b.max).length,
      color: b.color,
      fill: b.color,
    })).filter((b) => b.value > 0);

    return (
      <Fade in key="statistics">
        <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, color: theme.palette.primary.main }}>
            Statistik
          </Typography>

          {/* Big numbers */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            <Card sx={{ flex: '1 1 180px', textAlign: 'center' }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: theme.palette.primary.main, lineHeight: 1 }}>
                  {schwachstellenList.length}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.disabled', fontSize: '0.6rem' }}>
                  Schwachstellen
                </Typography>
              </CardContent>
            </Card>
            {avgScore !== null && (
              <Card sx={{ flex: '1 1 180px', textAlign: 'center' }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: getCvssSeverityColor(avgScore), lineHeight: 1 }}>
                    {avgScore.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.disabled', fontSize: '0.6rem' }}>
                    Durchschnittlicher CVSS
                  </Typography>
                </CardContent>
              </Card>
            )}
            {maxScore !== null && (
              <Card sx={{ flex: '1 1 180px', textAlign: 'center' }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: getCvssSeverityColor(maxScore), lineHeight: 1 }}>
                    {maxScore.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.disabled', fontSize: '0.6rem' }}>
                    Höchster CVSS
                  </Typography>
                </CardContent>
              </Card>
            )}
            {minScore !== null && (
              <Card sx={{ flex: '1 1 180px', textAlign: 'center' }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: getCvssSeverityColor(minScore), lineHeight: 1 }}>
                    {minScore.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.disabled', fontSize: '0.6rem' }}>
                    Niedrigster CVSS
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Charts */}
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', mb: 4 }}>
            {/* Kritikalität Pie */}
            {kritData.length > 0 && (
              <Card sx={{ flex: '1 1 340px', maxWidth: 420 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 1.5 }}>
                    Verteilung nach Kritikalität
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={kritData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        paddingAngle={2}
                        strokeWidth={0}
                      >
                        {kritData.map((entry, i) => (
                          <Cell key={`${entry.name}-${i}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                    {kritData.map((entry) => (
                      <Box key={entry.name} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
                        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 500, color: 'text.secondary' }}>
                          {entry.name}: {entry.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* CVSS Buckets Bar */}
            {bucketData.length > 0 && (
              <Card sx={{ flex: '1 1 340px', maxWidth: 420 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 1.5 }}>
                    CVSS-Score Verteilung
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={bucketData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#2e3560' : '#e0e0e0'} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: isDark ? '#8899aa' : '#5f7082' }} interval={0} angle={-15} textAnchor="end" height={50} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: isDark ? '#8899aa' : '#5f7082' }} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {bucketData.map((entry, i) => (
                          <Cell key={`bar-${i}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Review status */}
          {reviewData.length > 0 && (
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 1.5 }}>
                  Review-Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {reviewData.map((entry) => (
                    <Box key={entry.name} sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: entry.color }}>
                        {entry.value}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.72rem' }}>
                        {entry.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Fade>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     Finding detail panel
     ═══════════════════════════════════════════════════════════════ */
  const renderFinding = (findingIndex: number) => {
    const listItem = schwachstellenList[findingIndex];
    if (!listItem) return null;

    const detail = schwachstellenDetails[listItem.id];
    const referenzId = buildReferenzId(audit?.projektkennung, findingIndex);

    const klasseName = listItem.schwachstellenklasse
      ? (audit?.sprache === 'en' && listItem.schwachstellenklasse.name_en
        ? listItem.schwachstellenklasse.name_en
        : listItem.schwachstellenklasse.name_de)
      : '';
    const heading = [klasseName, listItem.titel].filter(Boolean).join(' – ');

    const kritColor = getKritikalitaetHexColor(listItem.kritikalitaet);
    const formattedKrit = formatSeverityLower(listItem.kritikalitaet);

    return (
      <Fade in key={`finding-${listItem.id}`}>
        <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: 3 }}>
          {/* Referenz-ID Badge */}
          <Chip
            label={referenzId}
            size="small"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              fontSize: '0.75rem',
              mb: 1,
              bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          />

          {/* Heading */}
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
            {heading || 'Schwachstelle'}
          </Typography>

          {/* Metadata table */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Table size="small">
                <TableBody>
                  {/* Kritikalität */}
                  <TableRow>
                    <TableCell sx={labelCellSx}>Kritikalität</TableCell>
                    <TableCell sx={valueCellSx}>
                      {formattedKrit ? (
                        <Chip
                          label={formattedKrit}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: `${kritColor}20`,
                            color: kritColor,
                            border: `1px solid ${kritColor}40`,
                          }}
                        />
                      ) : '–'}
                    </TableCell>
                  </TableRow>

                  {/* CVSS 3.1 */}
                  <TableRow>
                    <TableCell sx={labelCellSx}>CVSS 3.1</TableCell>
                    <TableCell sx={valueCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {listItem.cvss31_vector && (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {listItem.cvss31_vector}
                          </Typography>
                        )}
                        {listItem.cvss31_score != null && (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                              {listItem.cvss31_score.toFixed(1)}
                            </Typography>
                            <Chip
                              label={getCvssSeverityLabelLower(listItem.cvss31_score)}
                              size="small"
                              sx={{
                                bgcolor: getCvssSeverityColor(listItem.cvss31_score),
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          </>
                        )}
                        {!listItem.cvss31_vector && listItem.cvss31_score == null && '–'}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Schwachstellenklasse */}
                  <TableRow>
                    <TableCell sx={labelCellSx}>Schwachstellenklasse</TableCell>
                    <TableCell sx={valueCellSx}>
                      {klasseName || '–'}
                    </TableCell>
                  </TableRow>

                  {/* CWE */}
                  <TableRow>
                    <TableCell sx={labelCellSx}>CWE</TableCell>
                    <TableCell sx={valueCellSx}>
                      {listItem.cwe
                        ? `CWE-${listItem.cwe.id}: ${listItem.cwe.name}`
                        : '–'}
                    </TableCell>
                  </TableRow>

                  {/* Host */}
                  <TableRow>
                    <TableCell sx={labelCellSx}>Host</TableCell>
                    <TableCell sx={valueCellSx}>
                      {listItem.host || '–'}
                    </TableCell>
                  </TableRow>

                  {/* IP-Adresse */}
                  <TableRow>
                    <TableCell sx={labelCellSx}>IP-Adresse</TableCell>
                    <TableCell sx={valueCellSx}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {listItem.ip_adresse || '–'}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Risikoszenarien */}
                  {detail && detail.risikoszenarien && detail.risikoszenarien.length > 0 && (
                    <TableRow>
                      <TableCell sx={labelCellSx}>Risikoszenarien</TableCell>
                      <TableCell sx={valueCellSx}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {detail.risikoszenarien.map((r) => (
                            <Chip
                              key={r.id}
                              label={audit?.sprache === 'en' && r.name_en ? r.name_en : r.name_de}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Tools */}
                  {detail && detail.tools && detail.tools.length > 0 && (
                    <TableRow>
                      <TableCell sx={labelCellSx}>Tools</TableCell>
                      <TableCell sx={valueCellSx}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {detail.tools.map((t) => (
                            <Chip key={t.id} label={t.name} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Text sections */}
          {detail ? (
            <>
              {detail.allgemeine_beschreibung && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Allgemeine Beschreibung
                    </Typography>
                    <TipTapEditor
                      content={detail.allgemeine_beschreibung}
                      editable={false}
                      hidePageBreaks
                      minHeight={60}
                      findingReferences={findingReferences}
                      findingReferenceLang={findingReferenceLang}
                    />
                  </CardContent>
                </Card>
              )}

              {detail.detailbeschreibung && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Detailbeschreibung
                    </Typography>
                    <TipTapEditor
                      content={detail.detailbeschreibung}
                      editable={false}
                      hidePageBreaks
                      minHeight={60}
                      findingReferences={findingReferences}
                      findingReferenceLang={findingReferenceLang}
                    />
                  </CardContent>
                </Card>
              )}

              {detail.mitigationsmassnahmen && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Mitigationsmaßnahmen
                    </Typography>
                    <TipTapEditor
                      content={detail.mitigationsmassnahmen}
                      editable={false}
                      hidePageBreaks
                      minHeight={60}
                      findingReferences={findingReferences}
                      findingReferenceLang={findingReferenceLang}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Screenshot / Anhänge Gallery */}
              {(() => {
                const allFiles = schwachstellenFiles[listItem.id] || [];
                if (allFiles.length === 0) return null;
                const usedIds = getUsedFileIds(detail);
                return (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PhotoLibraryIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Screenshots / Anhänge
                        </Typography>
                        <Chip label={allFiles.length} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {allFiles.map((file, fileIdx) => {
                          const used = usedIds.has(file.id);
                          return (
                            <Box
                              key={file.id}
                              onClick={() => openLightbox(allFiles, fileIdx, usedIds)}
                              sx={{
                                width: 140,
                                cursor: 'pointer',
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                border: `2px solid ${used ? alpha(theme.palette.primary.main, 0.4) : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                                position: 'relative',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: theme.palette.primary.main,
                                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                                  transform: 'translateY(-2px)',
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src={filesService.getPreviewUrl(file.id)}
                                alt={file.dateiname}
                                sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                              />
                              {used && (
                                <Tooltip title="Im Text referenziert" arrow>
                                  <CheckCircleIcon sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    fontSize: 16,
                                    color: theme.palette.primary.main,
                                    bgcolor: isDark ? '#181c34' : '#fff',
                                    borderRadius: '50%',
                                  }} />
                                </Tooltip>
                              )}
                              <Typography variant="caption" noWrap sx={{
                                display: 'block',
                                px: 0.75,
                                py: 0.5,
                                fontSize: '0.6rem',
                                color: 'text.secondary',
                                bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
                              }} title={file.dateiname}>
                                {file.dateiname}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })()}
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Fade>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     Main content switch
     ═══════════════════════════════════════════════════════════════ */
  const renderContent = () => {
    if (selection === 'overview') return renderOverview();
    if (selection === 'statistics') return renderStatistics();
    if (typeof selection === 'number') return renderFinding(selection);
    return null;
  };

  /* ═══════════════════════════════════════════════════════════════
     Loading
     ═══════════════════════════════════════════════════════════════ */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!audit) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Typography>Audit nicht gefunden.</Typography>
      </Box>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          height: HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          gap: 1.5,
          bgcolor: isDark ? '#1a1e38' : '#fff',
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Zurück zum Audit">
          <IconButton size="small" onClick={() => navigate(`/audits/${id}`)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <SecurityIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          STARK
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }} noWrap>
          {audit.name}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="In neuem Tab öffnen">
          <IconButton
            size="small"
            onClick={() => window.open(`/audits/${id}/demo`, '_blank', 'noopener')}
            sx={{ color: 'text.secondary' }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Body ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ── Left rail ──────────────────────────────────────── */}
        <Box
          sx={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            bgcolor: isDark ? '#1c203b' : '#fafbfc',
            borderRight: `1px solid ${theme.palette.divider}`,
            overflowY: 'auto',
            overflowX: 'hidden',
            py: 1,
            px: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.3,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? '#3b4370' : '#c8cfd8', borderRadius: 2 },
          }}
        >
          {/* Übersicht */}
          <Box sx={navBtnSx(selection === 'overview')} onClick={() => setSelection('overview')}>
            <DashboardOutlinedIcon sx={{ fontSize: 20, color: selection === 'overview' ? theme.palette.primary.main : 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: selection === 'overview' ? theme.palette.primary.main : 'text.secondary' }}>
              Übersicht
            </Typography>
          </Box>

          {/* Statistik */}
          <Box sx={navBtnSx(selection === 'statistics')} onClick={() => setSelection('statistics')}>
            <BarChartOutlinedIcon sx={{ fontSize: 20, color: selection === 'statistics' ? theme.palette.primary.main : 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: selection === 'statistics' ? theme.palette.primary.main : 'text.secondary' }}>
              Statistik
            </Typography>
          </Box>

          {schwachstellenList.length > 0 && (
            <Divider sx={{ my: 0.75 }} />
          )}

          {/* Finding buttons */}
          {schwachstellenList.map((s, idx) => {
            const active = selection === idx;
            const kritColor = getKritikalitaetHexColor(s.kritikalitaet);
            const formattedKrit = formatSeverityLower(s.kritikalitaet);
            const refId = buildReferenzId(audit?.projektkennung, idx);
            return (
              <Box key={s.id} sx={navBtnSx(active, kritColor)} onClick={() => setSelection(idx)}>
                {/* Number badge */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: active ? `${kritColor}25` : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: `1.5px solid ${active ? kritColor : 'transparent'}`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', lineHeight: 1, color: active ? kritColor : 'text.primary' }}>
                    {idx + 1}
                  </Typography>
                </Box>
                {/* Info */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.3, color: active ? 'text.primary' : 'text.secondary' }}
                    title={s.titel}
                  >
                    {s.titel || '–'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: kritColor, flexShrink: 0 }} />
                    <Typography variant="caption" noWrap sx={{ fontSize: '0.6rem', color: 'text.disabled', fontWeight: 500 }}>
                      {formattedKrit || '–'}
                    </Typography>
                    {s.cvss31_score != null && (
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: getCvssSeverityColor(s.cvss31_score), ml: 0.25 }}>
                        {s.cvss31_score.toFixed(1)}
                      </Typography>
                    )}
                    {s.host && (
                      <Typography variant="caption" noWrap sx={{ fontSize: '0.55rem', color: 'text.disabled', fontFamily: 'monospace', ml: 0.25 }}>
                        {s.host}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" noWrap sx={{ fontSize: '0.55rem', color: 'text.disabled', fontFamily: 'monospace', display: 'block', mt: 0.1 }}>
                    {refId}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── Main content ───────────────────────────────────── */}
        <Box
          ref={contentRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            minWidth: 0,
            '& .ProseMirror img': {
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 2px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                transform: 'scale(1.01)',
              },
            },
          }}
        >
          {renderContent()}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
         Lightbox Dialog
         ═══════════════════════════════════════════════════════════ */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#0d1025' : '#111',
            width: '92vw',
            height: '90vh',
            maxWidth: '92vw',
            maxHeight: '90vh',
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: '#aaa', fontWeight: 500, flex: 1 }} noWrap>
            {lightboxImages[lightboxIndex]?.dateiname || ''}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', mr: 2 }}>
            {lightboxImages.length > 0 ? `${lightboxIndex + 1} / ${lightboxImages.length}` : ''}
          </Typography>
          <IconButton size="small" onClick={() => setLightboxOpen(false)} sx={{ color: '#aaa' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Main image area */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 0, px: 2 }}>
          {lightboxImages.length > 1 && (
            <IconButton
              onClick={() => setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length)}
              sx={{ position: 'absolute', left: 8, zIndex: 2, color: '#ccc', bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {lightboxImages[lightboxIndex] && (
            <Fade in key={lightboxImages[lightboxIndex].id}>
              <Box
                component="img"
                src={filesService.getPreviewUrl(lightboxImages[lightboxIndex].id)}
                alt={lightboxImages[lightboxIndex].dateiname}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            </Fade>
          )}

          {lightboxImages.length > 1 && (
            <IconButton
              onClick={() => setLightboxIndex((prev) => (prev + 1) % lightboxImages.length)}
              sx={{ position: 'absolute', right: 8, zIndex: 2, color: '#ccc', bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>

        {/* Thumbnail strip */}
        {lightboxImages.length > 1 && (
          <Box sx={{
            flexShrink: 0,
            display: 'flex',
            gap: 1,
            px: 2,
            py: 1.5,
            overflowX: 'auto',
            justifyContent: 'center',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
          }}>
            {lightboxImages.map((file, i) => {
              const isActive = i === lightboxIndex;
              const isUsed = lightboxUsedIds.has(file.id);
              return (
                <Box
                  key={file.id}
                  onClick={() => setLightboxIndex(i)}
                  sx={{
                    width: 72,
                    height: 52,
                    flexShrink: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: isActive ? '2px solid #3dd47e' : '2px solid transparent',
                    opacity: isActive ? 1 : 0.6,
                    transition: 'all 0.15s ease',
                    position: 'relative',
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={filesService.getPreviewUrl(file.id)}
                    alt={file.dateiname}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {isUsed && (
                    <CheckCircleIcon sx={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      fontSize: 12,
                      color: '#3dd47e',
                      bgcolor: 'rgba(0,0,0,0.6)',
                      borderRadius: '50%',
                    }} />
                  )}
                  {!isUsed && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: '1.5px solid rgba(255,255,255,0.4)',
                      bgcolor: 'rgba(0,0,0,0.4)',
                    }} />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default AuditDemoModePage;
